import { useCallback, useEffect, useState } from 'react';
import {
  CURRENCY_CATALOGUE,
  formatHours,
  formatMoney,
  reportTimelineResultSchema,
  type ReportDefinition,
  type ReportTimelineCell,
  type ReportTimelineResult,
  type TimelineDimension,
  type TimelineExtraKind,
} from '@forecastify/shared';
import { DataList, type DataListColumn, type DataListRow } from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { REPORT_TIMELINE_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { listLabels } from '../delivery/clients-screen';
import { envelopeOf, messageFor } from './report-errors';
import { usePages } from '../use-pages';
import { useWorkspaceNavigate } from '../../workspace-path';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · A TIMELINE REPORT — rows by periods, summed across a row.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The old product's `timelog_report.xlsx`, as a screen: the row dimensions' labels (a task or a
 * line item carries its code in a column of its own, as the sheet did), one column per week, month
 * or quarter, the row total, then the extras. The last row is the whole report's totals — the
 * server's, over every row the filters leave, not this page's.
 *
 * ⚠️ **Nothing is stored**, like a list: every page is a read of live data, re-read when any of it
 * moves (`REPORT_TIMELINE_TOPICS`).
 *
 * ⚠️ **A Munkaidő period that can no longer be logged is marked** — its heading says *lejárt* and
 * its cells are muted, Kockázatkezelés's lapsed months.
 */

/** A row dimension's heading. One literal key per branch — the catalogue check reads only those. */
export function timelineDimensionLabel(
  t: Translate,
  dimension: TimelineDimension,
  source: string,
): string {
  if (dimension === 'assignee') {
    return source === 'workday' ? t('report.timeline.person') : t('report.filter.loggedBy');
  }
  if (dimension === 'task') return t('report.dim.task');
  if (dimension === 'lineitem') return t('report.dim.lineitem');
  if (dimension === 'contract') return t('report.dim.contract');
  if (dimension === 'project') return t('report.dim.project');
  if (dimension === 'client') return t('report.dim.client');
  return t('report.dim.status');
}

/** The dimensions that carry a code beside their name — a task's key, a line item's project code. */
function hasCode(dimension: TimelineDimension): boolean {
  return dimension === 'task' || dimension === 'lineitem' || dimension === 'contract';
}

/** What a group with no value is called, per dimension. */
function noneName(t: Translate, dimension: TimelineDimension): string {
  if (dimension === 'project') return t('report.group.noProject');
  if (dimension === 'client') return t('report.group.internal');
  if (dimension === 'lineitem') return t('report.group.noLineItem');
  if (dimension === 'contract') return t('report.group.noContract');
  return t('report.group.noValue');
}

export function extraHeading(
  t: Translate,
  kind: TimelineExtraKind,
  dimension: TimelineDimension,
): string {
  if (kind === 'count') {
    if (dimension === 'task') return t('report.extra.countTask');
    if (dimension === 'assignee') return t('report.extra.countAssignee');
    if (dimension === 'lineitem') return t('report.extra.countLineitem');
    return t('report.extra.countProject');
  }
  if (dimension === 'task') return t('report.extra.listTask');
  if (dimension === 'assignee') return t('report.extra.listAssignee');
  if (dimension === 'lineitem') return t('report.extra.listLineitem');
  return t('report.extra.listProject');
}

/** A period column's heading, in the reader's locale. */
function periodHeading(t: Translate, locale: string, key: string, granularity: string): string {
  const date = new Date(`${key}T00:00:00Z`);
  if (granularity === 'month') {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(date);
  }
  if (granularity === 'quarter') {
    return t('report.timeline.quarter', {
      year: key.slice(0, 4),
      quarter: Math.floor((Number(key.slice(5, 7)) - 1) / 3) + 1,
    });
  }
  /* A week: its ISO number and its Monday. */
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() + 3);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return t('report.timeline.week', {
    week,
    day: new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date),
  });
}

export function ReportTimelineView({
  definition,
  reloadKey,
}: {
  readonly definition: ReportDefinition;
  /** Bumped by the header's Refresh, so a saved timeline re-reads on request too. */
  readonly reloadKey: number;
}) {
  const t = useT();
  const { locale } = useI18n();
  const session = useSession();
  const go = useWorkspaceNavigate();
  const pages = usePages();
  const spec = definition.timeline;

  const [result, setResult] = useState<ReportTimelineResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const definitionKey = JSON.stringify(definition);
  const { offset, limit, settle, reset } = pages;

  /* A changed definition starts at page one. */
  useEffect(() => {
    reset();
  }, [definitionKey, reset]);

  const load = useCallback(async () => {
    const response = await session
      .request('/reports/timeline', {
        method: 'POST',
        body: JSON.stringify({ definition: JSON.parse(definitionKey) as unknown, offset, limit }),
      })
      .catch(() => null);
    setLoading(false);
    if (response === null) {
      setError(t('error.unavailable'));
      return;
    }
    if (!response.ok) {
      const { code, detail } = await envelopeOf(response);
      setError(
        code === 'money_reports_not_allowed'
          ? t('report.money.lockedNote')
          : messageFor(t, code, detail),
      );
      return;
    }
    const parsed = reportTimelineResultSchema.safeParse(await response.json());
    if (!parsed.success) {
      setError(t('error.unavailable'));
      return;
    }
    setError(null);
    setResult(parsed.data);
    settle(parsed.data.meta.total);
  }, [session, definitionKey, offset, limit, settle, t]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  useLiveInvalidation(REPORT_TIMELINE_TOPICS, load);

  if (spec === undefined) return null;

  const currency = CURRENCY_CATALOGUE.find((one) => one.code === result?.currencyCode) ?? {
    code: result?.currencyCode ?? 'HUF',
    symbol: result?.currencyCode ?? 'Ft',
  };
  const figure = (value: string): string => {
    if (definition.metric === 'count') return Number(value).toLocaleString(locale);
    if (definition.metric === 'missingCost') return formatMoney(Number(value), currency);
    /* ⚠️ An empty period reads as a dash, not `0:00` — the old sheet's zeros were noise. */
    return Number(value) === 0 ? '–' : formatHours(value);
  };

  /* ── the columns: labels (and codes), periods, total, extras ────────────────────────────── */
  const labelColumns: DataListColumn[] = spec.rows.flatMap((dimension, at) => [
    ...(hasCode(dimension)
      ? [{ key: `code${String(at)}`, label: t('report.timeline.code'), width: 120 }]
      : []),
    { key: `label${String(at)}`, label: timelineDimensionLabel(t, dimension, definition.source) },
  ]);
  const periodColumns: DataListColumn[] = (result?.columns ?? []).map((column) => ({
    key: `p:${column.key}`,
    label: column.lapsed
      ? `${periodHeading(t, locale, column.key, spec.granularity)} · ${t('report.timeline.lapsed')}`
      : periodHeading(t, locale, column.key, spec.granularity),
    align: 'right',
    ...(column.lapsed ? { tip: t('report.timeline.lapsedTip') } : {}),
  }));
  const columns: DataListColumn[] = [
    ...labelColumns,
    ...periodColumns,
    { key: 'total', label: t('report.timeline.total'), align: 'right' },
    ...spec.extras.map((extra, at) => ({
      key: `extra${String(at)}`,
      label: extraHeading(t, extra.kind, extra.dimension),
      ...(extra.kind === 'count' ? { align: 'right' as const } : {}),
    })),
  ];

  const lapsedAt = new Set(
    (result?.columns ?? []).filter((column) => column.lapsed).map((column) => column.key),
  );

  /** A row's label cells — the code (where its dimension has one) and the name, per dimension. */
  const labelCells = (cells: readonly ReportTimelineCell[]): DataListRow['cells'] => {
    const out: DataListRow['cells'] = {};
    cells.forEach((cell, at) => {
      const dimension = spec.rows[at] ?? 'assignee';
      if (hasCode(dimension)) out[`code${String(at)}`] = { text: cell.code ?? '–', muted: true };
      out[`label${String(at)}`] = cell.label ?? noneName(t, dimension);
    });
    return out;
  };

  const rows: DataListRow[] = (result?.rows ?? []).map((row) => ({
    id: row.id,
    cells: {
      ...labelCells(row.cells),
      ...Object.fromEntries(
        (result?.columns ?? []).map((column, at) => [
          `p:${column.key}`,
          { text: figure(row.values[at] ?? '0'), muted: lapsedAt.has(column.key) },
        ]),
      ),
      total: { text: figure(row.total), bold: true },
      ...Object.fromEntries(
        spec.extras.map((_extra, at) => [`extra${String(at)}`, row.extras[at] ?? '']),
      ),
    },
  }));

  /* ⚠️ The whole report's totals, as the last row of every page — the server summed every row. */
  if (result !== null && result.rows.length > 0) {
    rows.push({
      id: 'totals',
      cells: {
        [labelColumns[0]?.key ?? 'label0']: { text: t('report.timeline.total'), bold: true },
        ...Object.fromEntries(
          result.columns.map((column, at) => [
            `p:${column.key}`,
            { text: figure(result.totals.values[at] ?? '0'), bold: true },
          ]),
        ),
        total: { text: figure(result.totals.total), bold: true },
      },
    });
  }

  const pathOf = new Map(
    (result?.rows ?? []).map((row) => [
      row.id,
      row.cells.find((cell) => cell.path !== null)?.path ?? null,
    ]),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-screen-part="report-timeline">
      {error !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mx-4 my-2 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {error}
        </p>
      )}

      <DataList
        toolbar={false}
        selectable={false}
        columns={columns}
        rows={rows}
        labels={listLabels(t)}
        isLoading={loading}
        onRowClick={(id) => {
          const path = pathOf.get(id);
          if (path !== null && path !== undefined) go(path);
        }}
        empty={{ title: t('report.list.empty'), body: t('report.list.emptyBody') }}
        page={{
          hasPrevious: pages.hasPrevious,
          hasNext: pages.hasNext,
          onPrevious: pages.previous,
          onNext: pages.next,
          index: pages.index,
          count: pages.count,
          total: pages.total,
          onGoTo: pages.goTo,
        }}
      />
    </div>
  );
}
